import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import { THERAPISTS, OFFICE_STAFF, CONTACT } from '../mock';

export default function Team() {
  return (
    <>
      <Seo
        title="Our Team of Therapists in Chesapeake, VA"
        description="Meet the compassionate, experienced team at Chesapeake Counseling Center. Licensed clinicians specializing in trauma, EMDR, anxiety, couples & family therapy."
        path="/team"
      />
      <PageHero
        eyebrow="Meet the Team"
        title="A Team You"
        highlight="Can Trust"
        subtitle="Experienced, compassionate, and dedicated to walking alongside you."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Meet the Team' }]}
      />

      {/* Therapists */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Our Clinicians</div>
            <h2 className="section-title mb-3">Our <span className="text-[color:var(--dusty-rose)]">Therapists</span></h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {THERAPISTS.map((t) => (
              <Link key={t.slug} to={`/team/${t.slug}`} className="card-soft lift-card overflow-hidden group reveal-scale block">
                <div className="aspect-[4/5] overflow-hidden bg-[color:var(--cream)] zoom-frame relative">
                  <img src={t.photo} alt={t.name} className="w-full h-full object-cover" />
                  {t.photoNeeded && (
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/85 text-[color:var(--taupe-gray)] font-semibold">Photo coming soon</span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] font-bold leading-tight">{t.name}</h3>
                  <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--sage-dark)] mt-1">{t.credentials}</div>
                  <div className="text-sm text-[color:var(--dusty-rose)] mt-1 font-semibold">{t.role}</div>
                  <p className="text-sm text-[color:var(--deep-sage)]/85 mt-3 leading-relaxed line-clamp-4">{t.bioShort}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {t.specialties.slice(0, 3).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: 'rgba(184,190,164,0.25)', color: 'var(--sage-dark)' }}>{s}</span>
                    ))}
                  </div>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-[color:var(--dusty-rose)] font-semibold">
                    Meet {t.name.split(' ')[0]} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Office Staff */}
      {OFFICE_STAFF && OFFICE_STAFF.length > 0 && (
        <section className="section-pad" style={{ background: 'var(--cream)' }}>
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-center mb-10 reveal">
              <div className="eyebrow mb-3">Meet Our Office</div>
              <h2 className="section-title mb-3">The Warm <span className="text-[color:var(--dusty-rose)]">Welcome</span> Behind the Scenes</h2>
              <p className="text-[color:var(--taupe-gray)] max-w-xl mx-auto">The people who keep our practice running smoothly and help you feel at home from your first call.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto stagger">
              {OFFICE_STAFF.map((p) => (
                <div key={p.slug} className="card-soft lift-card overflow-hidden group reveal-scale">
                  <div className="aspect-[4/5] overflow-hidden bg-[color:var(--warm-white)] zoom-frame relative">
                    <img src={p.photo} alt={p.name} className="w-full h-full object-cover" />
                    {p.photoNeeded && (
                      <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/85 text-[color:var(--taupe-gray)] font-semibold">Photo coming soon</span>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] font-bold leading-tight">{p.name}</h3>
                    {p.credentials && <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--sage-dark)] mt-1">{p.credentials}</div>}
                    <div className="text-sm text-[color:var(--dusty-rose)] mt-1 font-semibold">{p.role}</div>
                    <p className="text-sm text-[color:var(--deep-sage)]/85 mt-3 leading-relaxed">{p.bioShort}</p>

                    <div className="mt-5 pt-4 border-t flex flex-wrap gap-3 text-sm" style={{ borderColor: 'var(--soft-border)' }}>
                      <a href={CONTACT.phoneHref} className="inline-flex items-center gap-1.5 text-[color:var(--sage-dark)] hover:text-[color:var(--dusty-rose)]">
                        <Phone className="w-4 h-4" /> {CONTACT.phone}
                      </a>
                      <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-1.5 text-[color:var(--sage-dark)] hover:text-[color:var(--dusty-rose)]">
                        <Mail className="w-4 h-4" /> Email
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <FinalCTA />
    </>
  );
}
